import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ImageRightsAPI } from '../../api/ImageRightsAPI';
import { formatDate, removeTitle, sourceLabel, thumbnail, useArmedClick, whenLabel } from './pictureParts';

/** Requests carry at most this many pictures; the backend refuses more. */
const BATCH = 500;

const KINDS = [
  { id: 'DOMAIN', label: 'Webseite (Domain)', placeholder: 'flucc.at oder https://flucc.at/…' },
  { id: 'INSTAGRAM', label: 'Instagram-Account', placeholder: '@flucc' },
];

const TEXT = {
  WHITE: {
    name: 'Whitelist',
    other: 'Blacklist',
    rule: 'Quellen, bei denen du sicher bist, dass Schick alle Bilder zeigen darf. Ihre Bilder sammeln sich hier: '
      + 'du akzeptierst sie einzeln oder alle auf einmal. Darfst du nur einzelne Bilder zeigen, gib sie unter '
      + '„Einzelbilder“ frei. Eine Domain gilt für die Bilder dieser Webseite, ein Instagram-Account nur für seine Instagram-Posts.',
    add: 'Auf die Whitelist',
    notePlaceholder: 'Notiz (optional), z. B. Zusage per Mail 23.10.',
    noRules: 'Noch keine Quelle auf der Whitelist.',
    noPictures: 'Keine Bilder warten aufs Akzeptieren.',
  },
  BLACK: {
    name: 'Blacklist',
    other: 'Whitelist',
    rule: 'Quellen, deren Bilder Schick nicht zeigen darf. Ihre Bilder werden nie angezeigt, auch früher freigegebene nicht, '
      + 'und sammeln sich hier zum Entfernen – beim Event steht dann die Schick-Grafik. Eine Domain gilt für die Bilder '
      + 'dieser Webseite, ein Instagram-Account nur für seine Instagram-Posts.',
    add: 'Auf die Blacklist',
    notePlaceholder: 'Notiz (optional), z. B. Fotograf hat abgelehnt',
    noRules: 'Noch keine Quelle auf der Blacklist.',
    noPictures: 'Keine Bilder von Blacklist-Quellen – alles sauber.',
  },
};

const errorText = (err, fallback) => err?.response?.data?.message || fallback;

const inBatches = async (images, call) => {
  const results = [];
  for (let i = 0; i < images.length; i += BATCH) results.push(await call(images.slice(i, i + BATCH)));
  return results;
};

function RuleRow({ rule, onDelete, busy }) {
  const [armed, remove] = useArmedClick(() => onDelete(rule));
  return (
    <li className="isl-rule">
      <span className={'isl-kind isl-kind--' + (rule.kind === 'INSTAGRAM' ? 'ig' : 'web')}>
        {rule.kind === 'INSTAGRAM' ? 'Instagram' : 'Web'}
      </span>
      <span className="isl-rule-main">
        <strong className="isl-rule-label">{rule.label}</strong>
        {rule.note && <span className="isl-rule-note">{rule.note}</span>}
      </span>
      <span className="isl-rule-count">
        {rule.pictures} {rule.pictures === 1 ? 'Bild' : 'Bilder'}
        {rule.list === 'WHITE' && rule.waiting > 0 ? ` · ${rule.waiting} offen` : ''}
      </span>
      <button type="button" className={'pp-btn' + (armed ? ' pp-btn--armed' : '')} onClick={remove} disabled={busy}
        title="Von der Liste nehmen – die Bilder bleiben, wie sie sind">
        {armed ? 'Wirklich?' : 'Von Liste nehmen'}
      </button>
    </li>
  );
}

function ListedPicture({ picture, list, busy, onAccept, onRemove }) {
  const [removeArmed, remove] = useArmedClick(() => onRemove(picture));
  return (
    <article className={'ir-pic' + (busy ? ' ir-pic--busy' : '')}>
      <a className="ir-pic-image" href={picture.image} target="_blank" rel="noreferrer" title="Original öffnen">
        <img src={thumbnail(picture.image)} alt="" loading="lazy" />
      </a>
      <div className="ir-pic-body">
        <span className={'isl-badge isl-badge--' + list.toLowerCase()}>{TEXT[list].name} · {picture.listedBy}</span>
        <strong className="ir-pic-title" title={picture.title}>{picture.title}</strong>
        <span className="ir-pic-meta">
          {whenLabel(picture)}
          {picture.account ? `${whenLabel(picture) ? ' · ' : ''}@${picture.account}` : ''}
          {picture.cleared ? ` · freigegeben${picture.clearedAt ? ` am ${formatDate(picture.clearedAt)}` : ''}` : ''}
        </span>
        <a className={'irp-source irp-source--' + (picture.source === 'INSTAGRAM' ? 'ig' : 'web')}
          href={picture.image} target="_blank" rel="noreferrer" title={picture.image}>
          {sourceLabel(picture)}
        </a>
        {list === 'WHITE' ? (
          <button type="button" className="pp-btn isl-accept" onClick={() => onAccept(picture)} disabled={busy}>
            Akzeptieren
          </button>
        ) : (
          <button type="button" className={'pp-btn ' + (removeArmed ? 'pp-btn--armed' : 'pp-btn--danger')}
            onClick={remove} disabled={busy} title={removeTitle(picture)}>
            {removeArmed ? 'Wirklich entfernen?' : 'Entfernen'}
          </button>
        )}
      </div>
    </article>
  );
}

/**
 * One of the two lists: its sources (website domains, Instagram accounts) with a form to add
 * one, and the pictures from them – on the whitelist waiting to be accepted, on the blacklist
 * waiting to be removed.
 */
export default function SourceLists({ list, onChanged }) {
  const text = TEXT[list];
  const [rules, setRules] = useState(null);
  const [pictures, setPictures] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [busy, setBusy] = useState(() => new Set());
  const [allBusy, setAllBusy] = useState(false);
  const [form, setForm] = useState({ kind: 'DOMAIN', value: '', note: '' });
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [ruleList, pictureList] = await Promise.all([ImageRightsAPI.sources(), ImageRightsAPI.pictures()]);
      setRules(ruleList);
      setPictures(Array.isArray(pictureList) ? pictureList : []);
    } catch (err) {
      console.error('Fehler beim Laden der Listen:', err);
      setError('Konnte die Listen nicht laden.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const changed = () => { load(); if (onChanged) onChanged(); };

  const ownRules = useMemo(() => (rules ?? []).filter((rule) => rule.list === list), [rules, list]);
  const listed = useMemo(() => (pictures ?? []).filter((picture) => picture.listed === list), [pictures, list]);
  const waiting = list === 'WHITE' ? listed.filter((picture) => !picture.cleared) : listed;
  const acceptedCount = list === 'WHITE' ? listed.length - waiting.length : 0;

  const markBusy = (images, on) => setBusy((prev) => {
    const next = new Set(prev);
    images.forEach((image) => (on ? next.add(image) : next.delete(image)));
    return next;
  });

  const addRule = async (event) => {
    event.preventDefault();
    if (!form.value.trim()) return;
    setSaving(true);
    setError(null);
    setNotice(null);
    try {
      const { rule, withdrawn } = await ImageRightsAPI.saveSource({ ...form, list, note: form.note.trim() || null });
      // A source already on the other list keeps its entry and moves over.
      const moved = (rules ?? []).some((each) => each.id === rule.id && each.list !== list);
      const parts = [`${rule.label} steht auf der ${text.name}.`];
      if (moved) parts.push(`Von der ${text.other} hierher verschoben.`);
      if (withdrawn > 0) parts.push(`${withdrawn} ${withdrawn === 1 ? 'Freigabe' : 'Freigaben'} zurückgenommen.`);
      setNotice(parts.join(' '));
      setForm((prev) => ({ ...prev, value: '', note: '' }));
      changed();
    } catch (err) {
      setError(errorText(err, 'Konnte die Quelle nicht speichern.'));
    } finally {
      setSaving(false);
    }
  };

  const deleteRule = async (rule) => {
    setError(null);
    setNotice(null);
    try {
      await ImageRightsAPI.deleteSource(rule.id);
      setNotice(`${rule.label} ist nicht mehr auf der ${text.name}.`);
      changed();
    } catch (err) {
      setError(errorText(err, 'Konnte die Quelle nicht entfernen.'));
    }
  };

  const accept = async (images) => {
    markBusy(images, true);
    setError(null);
    setNotice(null);
    try {
      const results = await inBatches(images, ImageRightsAPI.acceptWhitelisted);
      const accepted = results.reduce((sum, r) => sum + (r?.accepted || 0), 0);
      setNotice(`${accepted} ${accepted === 1 ? 'Bild' : 'Bilder'} akzeptiert – Nutzer sehen ${accepted === 1 ? 'es' : 'sie'} jetzt.`);
      changed();
    } catch (err) {
      setError(errorText(err, 'Konnte die Bilder nicht freigeben.'));
    } finally {
      markBusy(images, false);
    }
  };

  const remove = async (images) => {
    markBusy(images, true);
    setError(null);
    setNotice(null);
    try {
      const results = await inBatches(images, ImageRightsAPI.removeBlacklisted);
      const removed = results.reduce((sum, r) => sum + (r?.removed || 0), 0);
      setNotice(`${removed} ${removed === 1 ? 'Bild' : 'Bilder'} entfernt – dort steht jetzt die Schick-Grafik.`);
      changed();
    } catch (err) {
      setError(errorText(err, 'Konnte die Bilder nicht entfernen.'));
    } finally {
      markBusy(images, false);
    }
  };

  const doAll = async () => {
    setAllBusy(true);
    const images = waiting.map((picture) => picture.image);
    await (list === 'WHITE' ? accept(images) : remove(images));
    setAllBusy(false);
  };
  const [allArmed, all] = useArmedClick(doAll);
  const kind = KINDS.find((k) => k.id === form.kind);

  return (
    <section aria-label={text.name}>
      <p className={'ir-rule isl-intro isl-intro--' + list.toLowerCase()}>{text.rule}</p>

      <div className="isl-sources">
        <form className="isl-form" onSubmit={addRule}>
          <div className="isl-kinds" role="radiogroup" aria-label="Art der Quelle">
            {KINDS.map((option) => (
              <button key={option.id} type="button" role="radio" aria-checked={form.kind === option.id}
                className={form.kind === option.id ? 'is-active' : ''}
                onClick={() => setForm((prev) => ({ ...prev, kind: option.id }))}>
                {option.label}
              </button>
            ))}
          </div>
          <input className="isl-input" aria-label={kind.label} placeholder={kind.placeholder} value={form.value}
            onChange={(event) => setForm((prev) => ({ ...prev, value: event.target.value }))} />
          <input className="isl-input isl-input--note" aria-label="Notiz" placeholder={text.notePlaceholder}
            value={form.note} maxLength={500}
            onChange={(event) => setForm((prev) => ({ ...prev, note: event.target.value }))} />
          <button type="submit" className={'btn-add ' + (list === 'WHITE' ? 'btn-add--green' : 'btn-add--danger')}
            disabled={saving || !form.value.trim()}>
            {saving ? 'Speichere …' : text.add}
          </button>
        </form>

        {rules && (ownRules.length === 0
          ? <p className="isl-empty">{text.noRules}</p>
          : (
            <ul className="isl-rules" aria-label={`Quellen auf der ${text.name}`}>
              {ownRules.map((rule) => <RuleRow key={rule.id} rule={rule} onDelete={deleteRule} busy={saving} />)}
            </ul>
          ))}
      </div>

      {notice && <p className="isl-notice" role="status">{notice}</p>}
      {error && <p className="pp-error" role="alert">{error}</p>}

      <div className="ir-toolbar isl-toolbar">
        <h3 className="isl-title">
          {list === 'WHITE'
            ? `${waiting.length} ${waiting.length === 1 ? 'Bild wartet' : 'Bilder warten'} aufs Akzeptieren`
            : `${waiting.length} ${waiting.length === 1 ? 'Bild' : 'Bilder'} von Blacklist-Quellen`}
          {acceptedCount > 0 && <span className="isl-title-sub"> · {acceptedCount} schon freigegeben</span>}
        </h3>
        <div className="ir-toolbar-right">
          {waiting.length > 0 && (
            <button type="button" disabled={allBusy}
              className={'btn-add ' + (allArmed ? 'btn-add--white' : list === 'WHITE' ? 'btn-add--green' : 'btn-add--danger')}
              onClick={all}>
              {allArmed
                ? (list === 'WHITE' ? `Wirklich alle ${waiting.length} akzeptieren?` : `Wirklich alle ${waiting.length} entfernen?`)
                : (list === 'WHITE' ? `Alle akzeptieren (${waiting.length})` : `Alle entfernen (${waiting.length})`)}
            </button>
          )}
          <button className="btn-add btn-add--white" type="button" onClick={load} disabled={loading}>Neu laden</button>
        </div>
      </div>

      {loading && !pictures ? (
        <div className="pp-sentinel"><div className="pp-spinner" aria-label="Lädt" /></div>
      ) : waiting.length === 0 ? (
        <div className="pp-empty">{text.noPictures}</div>
      ) : (
        <div className="ir-grid">
          {waiting.map((picture) => (
            <ListedPicture
              key={picture.image}
              picture={picture}
              list={list}
              busy={busy.has(picture.image)}
              onAccept={(each) => accept([each.image])}
              onRemove={(each) => remove([each.image])}
            />
          ))}
        </div>
      )}
    </section>
  );
}
